// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Calendar, Clock, MapPin, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO, isSameDay, addDays, startOfDay } from 'date-fns';
import { formatTime } from '@/lib/dates';
import { parseLocalDate } from '@/utils/formatters';

interface Tournament {
  id: string;
  name: string;
  tournament_type: string;
  start_date: string;
  end_date: string;
  sports?: {
    name: string;
  };
}

interface ScheduledMatch {
  id: string;
  round_number: number;
  match_number: number;
  bracket_type: string;
  team1_name?: string;
  team2_name?: string;
  team1_score?: number;
  team2_score?: number;
  status: string;
  scheduled_time?: string;
  court_field?: string;
  venues?: {
    name: string;
    city: string;
  };
}

interface VenueCourt {
  id: string;
  name: string;
}

const TournamentSchedulePage = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [matches, setMatches] = useState<ScheduledMatch[]>([]);
  const [courts, setCourts] = useState<VenueCourt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedCourt, setSelectedCourt] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (id) {
      fetchTournamentData();
      
      // Set up real-time subscription for match updates
      const channel = supabase
        .channel('tournament-schedule')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'tournament_matches',
            filter: `tournament_id=eq.${id}`
          },
          () => {
            fetchMatches();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [id]);

  const fetchTournamentData = async () => {
    try {
      await Promise.all([fetchTournament(), fetchMatches(), fetchCourts()]);
    } catch (error) {
      console.error('Error fetching tournament data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTournament = async () => {
    const { data, error } = await supabase
      .from('tournaments')
      .select(`
        *,
        sports(name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    setTournament(data);
    
    // Set initial date to tournament start date
    if (data.start_date) {
      setSelectedDate(parseLocalDate(data.start_date));
    }
  };

  const fetchMatches = async () => {
    const { data, error } = await supabase
      .from('tournament_matches')
      .select(`
        *,
        venues!venue_id(name, city)
      `)
      .eq('tournament_id', id)
      .not('scheduled_time', 'is', null)
      .order('scheduled_time');

    if (error) throw error;
    setMatches(data || []);
  };

  const fetchCourts = async () => {
    const { data: tournamentData, error: tournamentError } = await supabase
      .from('tournaments')
      .select('event_id')
      .eq('id', id)
      .single();

    if (tournamentError) throw tournamentError;

    const { data, error } = await supabase
      .from('event_courts')
      .select('id, name')
      .eq('event_id', tournamentData.event_id)
      .eq('is_active', true)
      .order('sort_order');

    if (error) throw error;
    setCourts(data || []);
  };

  const getMatchesForDate = (date: Date) => {
    return matches.filter(match => 
      match.scheduled_time && isSameDay(parseISO(match.scheduled_time), date)
    );
  };

  const getFilteredMatches = (dayMatches: ScheduledMatch[]) => {
    return dayMatches.filter(match => {
      const matchesSearch = 
        match.team1_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        match.team2_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        match.court_field?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCourt = selectedCourt === 'all' || match.court_field === selectedCourt;
      
      return matchesSearch && matchesCourt;
    });
  };

  const getMatchStatusBadge = (match: ScheduledMatch) => {
    if (match.status === 'completed') {
      return <Badge variant="outline">Final</Badge>;
    }
    if (match.status === 'in_progress') {
      return <Badge variant="destructive">Live</Badge>;
    }
    
    if (match.scheduled_time) {
      const matchTime = parseISO(match.scheduled_time);
      const now = new Date();
      if (matchTime < now && match.status === 'scheduled') {
        return <Badge variant="secondary">Starting Soon</Badge>;
      }
    }
    
    return <Badge variant="outline">Scheduled</Badge>;
  };

  const getTournamentDays = () => {
    if (!tournament) return [];
    
    const start = parseLocalDate(tournament.start_date);
    const end = parseLocalDate(tournament.end_date);
    const days = [];
    
    let current = startOfDay(start);
    while (current <= end) {
      days.push(current);
      current = addDays(current, 1);
    }
    
    return days;
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Loading schedule...</div>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center py-12">
          <Calendar className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Tournament not found</h3>
          <p className="text-muted-foreground mb-4">
            The tournament schedule you're looking for doesn't exist.
          </p>
          <Button asChild>
            <Link to="/tournaments">Browse Tournaments</Link>
          </Button>
        </div>
      </div>
    );
  }

  const tournamentDays = getTournamentDays();
  const dayMatches = getMatchesForDate(selectedDate);
  const filteredMatches = getFilteredMatches(dayMatches);

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/tournaments/${tournament.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Tournament
              </Link>
            </Button>
          </div>
          <h1 className="text-3xl font-bold">{tournament.name}</h1>
          <p className="text-lg text-muted-foreground">Tournament Schedule</p>
        </div>
      </div>

      {/* Date Navigation */}
      <Card>
        <CardHeader>
          <CardTitle>Tournament Days</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {tournamentDays.map((day, index) => {
              const dayMatches = getMatchesForDate(day);
              const isSelected = isSameDay(day, selectedDate);
              
              return (
                <Button
                  key={index}
                  variant={isSelected ? "default" : "outline"}
                  onClick={() => setSelectedDate(day)}
                  className="flex flex-col h-auto p-3"
                >
                  <span className="text-sm font-medium">
                    {format(day, 'EEE')}
                  </span>
                  <span className="text-xs">
                    {format(day, 'MMM d')}
                  </span>
                  {dayMatches.length > 0 && (
                    <Badge variant="secondary" className="text-xs mt-1">
                      {dayMatches.length} games
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search teams..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <Select value={selectedCourt} onValueChange={setSelectedCourt}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by court" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courts</SelectItem>
                {Array.from(new Set(matches.map(m => m.court_field).filter(Boolean))).map(court => (
                  <SelectItem key={court} value={court!}>
                    {court}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Schedule for Selected Date */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredMatches.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Games Scheduled</h3>
              <p className="text-muted-foreground">
                {dayMatches.length === 0 
                  ? 'No games are scheduled for this date.'
                  : 'No games match your current filters.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMatches.map((match) => (
                <div key={match.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getMatchStatusBadge(match)}
                      <Badge variant="outline" className="text-xs">
                        Round {match.round_number}
                      </Badge>
                      {match.bracket_type !== 'winners' && (
                        <Badge variant="outline" className="text-xs capitalize">
                          {match.bracket_type}
                        </Badge>
                      )}
                    </div>
                    {match.scheduled_time && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {formatTime(match.scheduled_time)}
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 items-center">
                    {/* Team 1 */}
                    <div className="text-center">
                      <p className="font-medium text-lg">
                        {match.team1_name || 'TBD'}
                      </p>
                      {match.status === 'completed' && match.team1_score !== undefined && (
                        <p className="text-2xl font-bold text-primary">
                          {match.team1_score}
                        </p>
                      )}
                    </div>

                    {/* VS */}
                    <div className="text-center">
                      <p className="text-muted-foreground font-medium">VS</p>
                    </div>

                    {/* Team 2 */}
                    <div className="text-center">
                      <p className="font-medium text-lg">
                        {match.team2_name || 'TBD'}
                      </p>
                      {match.status === 'completed' && match.team2_score !== undefined && (
                        <p className="text-2xl font-bold text-primary">
                          {match.team2_score}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Match Details */}
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {match.court_field && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {match.court_field}
                      </div>
                    )}
                    {match.venues && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {match.venues.name}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TournamentSchedulePage;